import { useEffect, useState } from "react";
import {
  deciderMesure,
  demanderMesure,
  getMesures,
  leverMesure,
} from "@/lib/apiProcedure";
import type {
  MesureProvisoire,
  StatutMesure,
  TypeMesure,
} from "@/types/procedure";

interface Props {
  caseId: string | number;
  /** Membre du dossier : peut demander une mesure. */
  canRequest?: boolean;
  /** Tribunal (ARBITRE/ADMIN) : peut accorder / rejeter / lever. */
  isTribunal?: boolean;
}

function apiDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback
  );
}

const STATUT_STYLE: Record<StatutMesure, string> = {
  DEMANDEE: "bg-amber-100 text-amber-800",
  ACCORDEE: "bg-green-100 text-green-800",
  REJETEE: "bg-red-100 text-red-800",
  LEVEE: "bg-gray-200 text-gray-700",
};

const TYPES: { value: TypeMesure; label: string }[] = [
  { value: "SEQUESTRE", label: "Séquestre" },
  { value: "CONSERVATION_PREUVES", label: "Conservation de preuves" },
  { value: "INTERDICTION_FAIRE", label: "Interdiction de faire" },
  { value: "GARANTIE_FRAIS", label: "Garantie des frais" },
  { value: "EXPERTISE_URGENTE", label: "Expertise urgente" },
  { value: "AUTRE", label: "Autre" },
];

/**
 * Mesures provisoires & conservatoires (AUA art. 13).
 * Partie : demande une mesure (urgence possible).
 * Tribunal : accorde / rejette, puis peut lever une mesure accordée.
 */
export function MesuresProvisoires({ caseId, canRequest, isTribunal }: Props) {
  const [mesures, setMesures] = useState<MesureProvisoire[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [type, setType] = useState<TypeMesure>("SEQUESTRE");
  const [objet, setObjet] = useState("");
  const [urgence, setUrgence] = useState(false);
  const [decId, setDecId] = useState<string | null>(null);
  const [decChoice, setDecChoice] = useState("ACCORDEE");
  const [decMotif, setDecMotif] = useState("");

  const load = () => {
    getMesures(caseId)
      .then((res) => setMesures(res.data))
      .catch(() => setMesures([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const wrap = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      load();
      return true;
    } catch (e) {
      setErr(apiDetail(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleDemander = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await wrap(
      () => demanderMesure(caseId, { type_mesure: type, objet, urgence }),
      "Échec de la demande de mesure.",
    );
    if (ok) {
      setObjet("");
      setUrgence(false);
      setType("SEQUESTRE");
    }
  };

  const handleDecider = async (id: string) => {
    const ok = await wrap(
      () =>
        deciderMesure(caseId, id, {
          decision: decChoice,
          motif: decMotif.trim() || undefined,
        }),
      "Échec de la décision.",
    );
    if (ok) {
      setDecId(null);
      setDecMotif("");
      setDecChoice("ACCORDEE");
    }
  };

  const handleLever = (id: string) =>
    wrap(() => leverMesure(caseId, id), "Échec de la levée de la mesure.");

  if (loading) return null;

  return (
    <div className="border rounded-lg p-4 mt-6 bg-white">
      <h3 className="text-lg font-bold text-gray-800 mb-1">
        Mesures provisoires & conservatoires
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Acte Uniforme relatif à l'Arbitrage, art. 13.
      </p>

      {err && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          {err}
        </div>
      )}

      {canRequest && (
        <form
          onSubmit={handleDemander}
          className="mb-5 p-3 bg-gray-50 rounded space-y-2"
        >
          <div className="text-xs font-medium text-gray-600">
            Demander une mesure au tribunal
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TypeMesure)}
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <textarea
            value={objet}
            onChange={(e) => setObjet(e.target.value)}
            required
            placeholder="Objet & motivation de la mesure demandée…"
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
            rows={2}
          />
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={urgence}
              onChange={(e) => setUrgence(e.target.checked)}
            />
            Demande en urgence
          </label>
          <button
            type="submit"
            disabled={busy || objet.trim().length < 10}
            className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
          >
            Soumettre la demande
          </button>
        </form>
      )}

      {mesures.length === 0 ? (
        <p className="text-sm text-gray-400 italic">
          Aucune mesure provisoire demandée pour le moment.
        </p>
      ) : (
        <div className="space-y-3">
          {mesures.map((m) => {
            const peutDecider = !!isTribunal && m.statut === "DEMANDEE";
            const peutLever = !!isTribunal && m.statut === "ACCORDEE";
            return (
              <div key={m.id} className="border rounded p-3 text-sm">
                <div className="flex justify-between items-start gap-3">
                  <div className="font-medium text-gray-800">
                    {TYPES.find((t) => t.value === m.type_mesure)?.label ??
                      m.type_mesure}
                    {m.urgence && (
                      <span className="ml-2 text-xs font-bold text-red-600">
                        URGENCE
                      </span>
                    )}
                  </div>
                  <span
                    className={`shrink-0 text-xs px-2 py-1 rounded-full font-bold ${STATUT_STYLE[m.statut]}`}
                  >
                    {m.statut}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{m.objet}</p>
                {m.decision_motif && (
                  <p className="text-xs text-gray-500 mt-1">
                    Motif : {m.decision_motif}
                  </p>
                )}

                {peutDecider && decId !== m.id && (
                  <button
                    onClick={() => {
                      setDecId(m.id);
                      setDecChoice("ACCORDEE");
                      setDecMotif("");
                    }}
                    className="mt-2 text-xs text-green-700 underline"
                  >
                    Trancher
                  </button>
                )}
                {peutDecider && decId === m.id && (
                  <div className="mt-2 space-y-2">
                    <select
                      value={decChoice}
                      onChange={(e) => setDecChoice(e.target.value)}
                      className="rounded border p-1.5 text-sm bg-white text-gray-900"
                    >
                      <option value="ACCORDEE">Accordée</option>
                      <option value="REJETEE">Rejetée</option>
                    </select>
                    <textarea
                      value={decMotif}
                      onChange={(e) => setDecMotif(e.target.value)}
                      placeholder="Motif (obligatoire si rejet)"
                      className="w-full rounded border p-2 text-sm bg-white text-gray-900"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={busy}
                        onClick={() => handleDecider(m.id)}
                        className="bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Rendre la décision
                      </button>
                      <button
                        onClick={() => setDecId(null)}
                        className="text-gray-500 text-xs"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {peutLever && (
                  <button
                    disabled={busy}
                    onClick={() => handleLever(m.id)}
                    className="mt-2 text-xs text-amber-700 underline disabled:opacity-50"
                  >
                    Lever la mesure
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
